// Adapted for Vite + React Router + react-i18next

import { ErrorQueryHandler } from 'src/components/error/error-query-handler.component'
import {
  CurecallDropdownButton,
  CurecallList,
  ECurecallDropdownPosition,
  CurecallDropdownItemStyle,
} from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { LocalizedLink } from 'src/components/navigation/localized-link.component'
import { ProjectService } from 'src/services/project.service'
import { useQuery } from '@tanstack/react-query'
import { TProject } from 'src/types/project'
import PageTitle from 'src/components/page-title/page-title.component'
import { twMerge } from 'src/utils/tw-merge.util'
import { useParams } from 'react-router-dom'

export const ProjectsPage = () => {
  const { projectId: currentProjectId } = useParams<{ projectId: string }>()
  const { isLoading, error, data } = useQuery({
    queryKey: ['project-list'],
    queryFn: () => ProjectService.getProjectsList(),
  })

  const { t } = useTranslation()

  if (error) return <ErrorQueryHandler error={error} />

  const tableRowsData =
    data?.map((project, index) => ({
      // Change the order will affect the table columns order
      ...project,
      name: {
        component: <RowLink nextProject={project} />,
        observedData: project.name,
      },
      actions: (
        <RowDropdown
          nextProjectId={project.id}
          currentProjectId={currentProjectId ?? ''}
          menuPosition={
            index < data?.length - 3
              ? ECurecallDropdownPosition['BOTTOM_LEFT']
              : ECurecallDropdownPosition['TOP_LEFT']
          }
        />
      ),
    })) ?? []

  return (
    <div className={'c-flex c-flex-col'}>
      <PageTitle>{t('pages.projects-page.title')}</PageTitle>
      <CurecallList
        isLoading={isLoading}
        loadingLabel={t('pages.projects-page.loading')}
        noDataLabel={t('pages.projects-page.no-data')}
        rowsData={tableRowsData}
        isSelectedRows={{ matchingHeader: 'id', matchingValue: currentProjectId }}
        defaultSortData={{ key: 'id', descOrder: false }}
        headersData={[
          { label: 'id', key: 'id', isSorted: true },
          {
            label: t('pages.projects-page.project-name'),
            key: 'name',
            isSorted: true,
          },
          { label: t('pages.projects-page.sms-sender'), key: 'smsSender' },
          { label: t('pages.projects-page.users-count'), key: 'usersCount' },
          { label: t('pages.projects-page.patients-count'), key: 'patientsCount' },
          { label: t('pages.projects-page.actions'), key: 'actions' },
        ]}
      />
    </div>
  )
}

const RowLink = ({ nextProject }: { nextProject: Pick<TProject, 'id' | 'name'> }) => {
  return (
    <LocalizedLink className="c-font-bold c-text-primary c-cursor-pointer" to={`/${nextProject.id}`}>
      {nextProject.name}
    </LocalizedLink>
  )
}

const RowDropdown = ({
  nextProjectId,
  currentProjectId,
  menuPosition,
}: {
  nextProjectId: number
  currentProjectId: string
  menuPosition: ECurecallDropdownPosition
}) => {
  const { t } = useTranslation()
  return (
    <CurecallDropdownButton
      dropdownButtonLabel={t('pages.projects-page.actions')}
      position={menuPosition}
      customItems={[
        <li key={`patients-link-project-${nextProjectId}`}>
          <LocalizedLink
            className={twMerge(CurecallDropdownItemStyle, 'hover:c-text-grey-700')}
            to={`/${nextProjectId}/medical/patients?previousUserProjectId=${currentProjectId}`}
          >
            {t('pages.projects-page.patients-link')}
          </LocalizedLink>
        </li>,
        <li key={`users-link-project-${nextProjectId}`}>
          <LocalizedLink
            className={twMerge(CurecallDropdownItemStyle, 'hover:c-text-grey-700')}
            to={`/${nextProjectId}/medical/users?previousUserProjectId=${currentProjectId}`}
          >
            {t('pages.projects-page.users-link')}
          </LocalizedLink>
        </li>,
      ]}
    />
  )
}
